import React from 'react';
import { useRouter } from 'next/router';
import Box from '@mui/material/Box';
import MuiPagination from '@mui/material/Pagination';
import type { PaginationProps as MuiPaginationProps } from '@mui/material/Pagination';
import { styled } from 'sensortower-components/src/utils/styled';
import ErrorBoundary from 'sensortower-components/src/base-components/ErrorBoundary';

import { parsePageNumber } from '../CollectionFiltered/hooks/useCollectionFilteredItems';
import { useLocalizationContext } from '../LocalizationContext';

export interface PaginationProps extends MuiPaginationProps {
  count: number;
  pageKey?: string;
}

export const Pagination = ({ count, pageKey = 'page', page: pageProp, onChange, ...props }: PaginationProps) => {
  const router = useRouter();
  const isControlled = pageProp !== undefined;
  const localization = useLocalizationContext();
  const pageFromRouter = parsePageNumber(router.query[pageKey]);

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    router.push({ pathname: router.pathname, query: { ...router.query, [pageKey]: String(value) } }, undefined, {
      shallow: true,
    });
    onChange?.(event, value);
  };

  return (
    <ErrorBoundary>
      <Root
        previousLabel={localization['common.paginationPreviousLabel']?.shortTextValue ?? 'Previous'}
        nextLabel={localization['common.paginationNextLabel']?.shortTextValue ?? 'Next'}
      >
        <MuiPagination
          data-testid={'Pagination-Root'}
          count={count}
          // TODO: Need to update primary palette, as this includes green color for active state
          color="primary"
          page={isControlled ? pageProp : pageFromRouter}
          onChange={handleChange}
          {...props}
        />
      </Root>
    </ErrorBoundary>
  );
};

export default Pagination;

const Root = styled(Box, {
  name: 'Pagination',
  slot: 'Root',
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'previousLabel' && prop !== 'nextLabel',
  overridesResolver: (_, styles) => ({
    ...(styles.root as any),
  }),
})<{ previousLabel: string; nextLabel: string; variant?: string }>(({ theme, previousLabel, nextLabel }) => ({
  '& .MuiPagination-ul': {
    [theme.breakpoints.up('md')]: {
      'li:first-of-type': {
        'button:after': {
          content: `'${previousLabel}'`,
          padding: '0px 10px 0px 10px',
        },
      },
      'li:last-of-type': {
        'button:before': {
          content: `'${nextLabel}'`,
          padding: '0px 10px 0px 10px',
        },
      },
    },
  },
  display: 'flex',
  justifyContent: 'center',
}));
